import { Box, Typography } from "@mui/material";
import LinkRouter from "components/navigation/LinkRouter";
import { PriorityChip } from "components/creation/PrioritySelect";
import { StageChip } from "components/tasks/workflowStates";
import { ProjectLabel } from "components/tasks/TaskRow";
import { dateStringToDayAndDate } from "components/tasks/utils";
import WithDividers from "components/helpers/WithDividers";
import useIsMobile from "components/hooks/useIsMobile";

export interface AddedTask {
  taskRef: string;
  taskTitle: string;
  taskState: string;
  taskPriority: string;
  taskStage: string;
  taskDueDate?: string;
  taskProject?: string;
  taskStageDate?: string;
}

export const TasksSection = ({ tasks }: { tasks: AddedTask[] }) => {
  const isMobile = useIsMobile();

  if (tasks.length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Typography variant="h4">Tasks</Typography>
      {tasks.map((task, index) => {
        return (
          <LinkRouter
            sx={{ padding: "0px" }}
            key={index}
            to={`/tasks/${encodeURIComponent(task.taskRef)}`}
          >
            <Box
              sx={(theme) => ({
                width: "100%",
                border: `1px solid ${theme.palette.gray[4]}`,
                borderRadius: "12px",
                padding: "16px 20px",
                transition: "background-color 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: theme.palette.gray[1],
                },
              })}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  width: "100%",
                  gap: "4px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    flex: 1,
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body"
                    color="text.primary"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {task.taskTitle}
                  </Typography>
                  <Typography
                    variant="bodySmall"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <WithDividers DividerComponent={() => <span>{" • "}</span>}>
                      {[
                        task.taskProject ? (
                          <ProjectLabel project={task.taskProject} key={0} />
                        ) : null,
                        task.taskDueDate ? (
                          <Typography
                            key={1}
                            variant="bodySmall"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >{`Due ${
                            dateStringToDayAndDate(task.taskDueDate, false)[1]
                          }`}</Typography>
                        ) : null,
                      ].filter(Boolean)}
                    </WithDividers>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    width: isMobile ? "100%" : "fit-content",
                    alignItems: "center",
                    flexDirection: isMobile ? "row" : "row-reverse",
                    gap: isMobile ? "8px" : "12px",
                  }}
                >
                  {task.taskPriority !== "medium" && (
                    <PriorityChip
                      priority={task.taskPriority}
                      isMobile={isMobile}
                    />
                  )}
                  <StageChip
                    stage={task.taskStage}
                    accountType="member"
                    stageDate={task.taskStageDate}
                    sx={{ flexShrink: 0 }}
                  />
                </Box>
              </Box>
            </Box>
          </LinkRouter>
        );
      })}
    </Box>
  );
};
